/**
 * @fileoverview Defines the `HowMakeSearchGood` component, a blog post discussing search evaluation for agentic workflows, focusing on recall and token usage. It explains the shift from traditional recall/precision metrics and its impact on coding agent efficiency. The component utilizes `Navbar`, `YoutubeVideo`, `BlogImage`, and `Contact` components.
 */

import BlogImage from '../../components/BlogImage'
import Contact from '../../components/Contact'
import { Navbar } from '../../components/Navbar'
import YoutubeVideo from '../../components/YoutubeVideo'
import '../../styles/pages/BlogPost.css'

export default function HowMakeSearchGood() {
  return (
    <div className="home-container">
      <Navbar pageName="Blog" pageNameLink="/blog" />
      <div className="blog-post-container">
        <h1>How make search good?</h1>
        <h4>Evaluating documentation search for coding agents.</h4>

        <p>
          How to make search good is something I've been thinking about on and off for the last
          couple years. Most recently in terms of how to help a coding agent find the context it
          needs to be successful. This post covers how we evaluate search quality in Ref and how the
          thinking has evolved over time.
        </p>

        <div className="blog-content">
          <YoutubeVideo
            videoId="t1ssthGJGYo"
            title="How make search good?"
            footer={
              'If you prefer the video version of this post, here it is. The content is the same.'
            }
          />

          <h2>The Platonic Ideal vs. Reality</h2>

          <p>
            The platonic idea of search is simple: you put in a query and get back exactly the
            document that has the information you need. This was the goal of Ref from the outset.
            The idea seems straightforward—input a query, get back exactly the piece of information
            you need.
          </p>

          <BlogImage
            src="/images/hmsg/platonic.png"
            alt="Platonic ideal of search"
            caption="The search simply returns the exact document for the query."
          />

          <p>
            But in reality, search is messier. You put in a query, you get a bunch of documents
            back, and you have to decide which ones are relevant and which ones to return. The way
            people generally build reliable search systems is with the evaluation metrics{' '}
            <strong>recall</strong> (did we find all the relevant documents?) and{' '}
            <strong>precision</strong> (of all the documents we returned, what fraction were
            actually relevant?) and <strong>F1 score</strong> (a combination of the two).
          </p>

          <BlogImage
            src="/images/hmsg/reality.png"
            alt="Real search"
            caption="Real searches return a bunch of documents that may or may not be relevant."
          />

          <p>
            For the first version of Ref, this was exactly the type of evaluation we were doing—very
            classic search evaluation. But as things have evolved, as we've seen the Ref MCP server
            in practice, our thinking has changed.
          </p>

          <h2>Agentic Search is Different</h2>

          <p>
            Agentic search behaves differently than classic query-to-document search. Agents will
            often issue multiple searches, refining their queries and iterating over time. When
            Claude Code looks at documentation with Ref, the search pattern is more iterative.
          </p>

          <p>
            The agent will do a search, then refine the query. Now it has some documents, it'll read
            a couple, then think "Okay, I need to look up something else" and refine the search
            again. Eventually it concludes "I've got everything I need, let's start coding."
          </p>

          <BlogImage
            src="/images/hmsg/agentic-search.png"
            alt="Agentic search"
            caption="Claude Code likes to explore when it's researching."
          />

          <p>
            This happens because foundation model companies are training their models to do research
            like humans would—search, refine based on what you learn, iterate and loop. This is what
            makes agentic search so powerful.
          </p>

          <p>
            But this revealed a problem with my evaluation approach. I had been building and
            evaluating for that first search: "Search for this query, does it get the right
            documents?" While that's a relevant sub-problem, what you really want to evaluate is the
            system as a whole.
          </p>

          <h2>New Metrics</h2>

          <p>
            We no longer evaluate with traditional recall and precision, we now use{' '}
            <strong>recall and token usage</strong>.
          </p>

          <BlogImage
            src="/images/hmsg/token-usage.png"
            alt="Recall and token usage"
            caption="We keep recall and replace precision with token usage."
          />

          <p>
            We still need recall because we need to find the right information. But what
            increasingly matters is how much of the context window we're using on research before we
            get to actually coding. There are two reasons for this:
          </p>

          <ol>
            <li>
              <strong>Cost:</strong> Tokens cost money and background agents are expensive.
            </li>
            <li>
              <strong>Context rot:</strong> As you add more context, models get less effective. You
              can't just throw stuff into the context window and assume performance will remain
              constant.
            </li>
          </ol>

          <p>
            If you squint, token usage and precision are still basically the same metric. Instead of
            calculating precision based on the documents returned, we calculate based on tokens
            used.
          </p>

          <h2>Building Better Evaluation</h2>

          <p>
            I've been building out Ref's eval suite around these two metrics. The inputs look
            basically the same—a query and some expected URLs (links to specific spots on pages
            we're trying to find). But instead of just running a query, we're running it in the MCP
            harness.
          </p>

          <p>
            An agent with an MCP server running Ref gets both search and read tools. We watch it
            search, refine, read pages, and ultimately decide it's found everything it needs.
            Throughout this process, I track token usage to understand how much context window we're
            consuming.
          </p>

          <h3>The 10,000 Token Baseline</h3>

          <p>For token usage scoring, I chose a baseline of 10,000 tokens for two reasons.</p>

          <p>
            First, there's Context7, a great free server for accessing documentation. It works by
            choosing a library and getting the 10,000 most relevant tokens of code snippets from
            their index. This gives us a baseline to beat.
          </p>

          <p>
            Second,{' '}
            <a href="https://research.trychroma.com/context-rot" target="_blank">
              research from the team at Chroma
            </a>{' '}
            shows that as we increase the context length, we start seeing degradation in result
            quality for the needle-in-the-haystack task. Looking at the graphs you can see around
            10,000 token context is where high quality models start to get worse. (Note for clarity:
            they do not make this claim in the paper, this is me eyeballing the graphs they present
            .)
          </p>

          <BlogImage
            src="/images/hmsg/needle-haystack.png"
            alt="Needle in the haystack results from Chroma research"
            caption="Needle in the haystack results from Chroma's research."
          />

          <h2>Results</h2>

          <p>
            The results have been encouraging. Ref saves on average 5447 tokens on a suite of 17
            real queries for popular libraries like Tailwind, Next.js and Shadcn as compared to our
            10k baseline from Context7. That's $0.082 per request at Claude Opus API pricing.
          </p>

          <p>
            In the best cases, we're saving 95% of tokens—using just 439 tokens to find information
            about Figma's post-comment API by doing a targeted search and reading only the relevant
            section.
          </p>

          <p>
            On the more complex end, queries like comparing different N8N nodes and best practices
            require more token usage, but we still see the model taking an efficient iterative
            approach: search, read, read, search—finding what it needs while minimizing token
            consumption.
          </p>

          <BlogImage
            src="/images/hmsg/eval-dashboard.png"
            alt="Eval dashboard"
            caption="Eval dashboard for the suite of 17 queries of popular libraries. The average is 5447 tokens saved."
          />

          <h2>Looking Forward</h2>

          <p>
            This approach to evaluation is aligned with how agents actually work. Rather than
            optimizing for theoretical perfection on individual queries, we're optimizing for
            practical efficiency across the entire research workflow.
          </p>

          <p>
            I'm excited about where this is heading and the results it's already driving. I'll dive
            deeper into the technical implementation in a future post.
          </p>
        </div>
      </div>
      <Contact />
    </div>
  )
}
